/**
 * WhatsApp Templates TypeScript Types
 *
 * Type definitions for WhatsApp template management system
 */

// ===================================================================
// ENUMS
// ===================================================================

export enum WhatsAppTemplateStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  DELETED = 'DELETED'
}

export enum WhatsAppTemplateCategory {
  MARKETING = 'MARKETING',
  UTILITY = 'UTILITY',
  AUTHENTICATION = 'AUTHENTICATION'
}

export enum SessionState {
  ACTIVE_24H = 'ACTIVE_24H',
  TEMPLATE_SENT = 'TEMPLATE_SENT',
  NO_RESPONSE = 'NO_RESPONSE',
  CLOSED = 'CLOSED'
}

export enum SequenceStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  STOPPED = 'stopped',
  FAILED = 'failed'
}

// ===================================================================
// WHATSAPP TEMPLATE TYPES
// ===================================================================

export interface WhatsAppTemplateButton {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';
  text: string;
  url?: string;
  phone_number?: string;
}

export interface WhatsAppTemplateHeader {
  type: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  text?: string;
  media_url?: string;
}

export interface WhatsAppTemplate {
  id: number;
  account_id: number;
  created_by_user_id?: number;
  name: string;
  category: WhatsAppTemplateCategory;
  language: string;
  status: WhatsAppTemplateStatus;
  body: string;
  header?: WhatsAppTemplateHeader;
  footer?: string;
  buttons?: WhatsAppTemplateButton[];
  variables?: string[];
  example_values?: Record<string, string>;
  provider_template_id?: string;
  submitted_at?: string;
  approved_at?: string;
  rejected_at?: string;
  rejection_reason?: string;
  usage_count: number;
  last_used_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TemplateCreateRequest {
  name: string;
  category: WhatsAppTemplateCategory;
  language: string;
  body: string;
  header?: WhatsAppTemplateHeader;
  footer?: string;
  buttons?: WhatsAppTemplateButton[];
  variables?: string[];
  example_values?: Record<string, string>;
  provider?: string;
}

export interface TemplateUpdateRequest {
  name?: string;
  body?: string;
  header?: WhatsAppTemplateHeader;
  footer?: string;
  buttons?: WhatsAppTemplateButton[];
  variables?: string[];
  example_values?: Record<string, string>;
  is_active?: boolean;
}

export interface TemplateSendRequest {
  conversation_id: number;
  variables: Record<string, any>;
  channel_id?: number;
}

// ===================================================================
// SESSION TYPES
// ===================================================================

export interface ConversationSession {
  id: number;
  conversation_id: number;
  session_state: SessionState;
  last_user_message_at?: string;
  session_expires_at?: string;
  last_template_sent_at?: string;
  last_template_id?: number;
  template_response_received: boolean;
  template_response_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SessionSummary {
  conversation_id: number;
  session_state: SessionState;
  can_send_freeform: boolean;
  requires_template: boolean;
  time_until_expiry_seconds?: number;
  last_user_message_at?: string;
  session_expires_at?: string;
  last_template_sent_at?: string;
  template_response_received: boolean;
}

// ===================================================================
// SEQUENCE TYPES
// ===================================================================

export interface SequenceStep {
  step: number;
  template_id: number;
  delay_hours: number;
  conditions?: {
    send_only_if_no_response?: boolean;
    stop_if_session_reopened?: boolean;
  };
}

export interface TemplateSequence {
  id: number;
  account_id: number;
  created_by_user_id?: number;
  name: string;
  description?: string;
  sequence_steps: SequenceStep[];
  trigger_type: 'manual' | 'session_closed' | 'no_response';
  trigger_conditions?: Record<string, any>;
  usage_count: number;
  success_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SequenceCreateRequest {
  name: string;
  description?: string;
  sequence_steps: SequenceStep[];
  trigger_type?: string;
  trigger_conditions?: Record<string, any>;
  is_active?: boolean;
}

export interface SequenceUpdateRequest {
  name?: string;
  description?: string;
  sequence_steps?: SequenceStep[];
  trigger_type?: string;
  trigger_conditions?: Record<string, any>;
  is_active?: boolean;
}

export interface SequenceStartRequest {
  contact_id: number;
  conversation_id?: number;
}

export interface ContactSequenceState {
  id: number;
  sequence_id: number;
  contact_id: number;
  conversation_id?: number;
  current_step: number;
  status: SequenceStatus;
  steps_sent: Record<string, any>[];
  user_responded: boolean;
  user_response_at?: string;
  next_step_at?: string;
  completed_at?: string;
  stopped_at?: string;
  stop_reason?: string;
  failed_at?: string;
  failure_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface SequenceStats {
  sequence_id: number;
  name: string;
  total_started: number;
  total_success: number;
  success_rate: number;
  active: number;
  completed: number;
  stopped: number;
  failed: number;
}

// ===================================================================
// CONFIGURATION TYPES
// ===================================================================

export interface WhatsAppConstraints {
  header: {
    text_max_length: number;
    media_types: string[];
  };
  body: {
    max_length: number;
    max_variables: number;
  };
  footer: {
    max_length: number;
    allow_variables: boolean;
  };
  buttons: {
    max_count: number;
    max_text_length: number;
    allowed_types: string[];
  };
  variables: {
    max_total: number;
    format: string;
  };
}

export interface SessionRules {
  session_duration_hours: number;
  allow_template_in_active_session: boolean;
  auto_close_expired_sessions: boolean;
  template_cooldown_minutes: number;
}

// ===================================================================
// API RESPONSE TYPES
// ===================================================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface TemplateListResponse {
  success: boolean;
  templates: WhatsAppTemplate[];
  count: number;
}

export interface TemplateResponse {
  success: boolean;
  template: WhatsAppTemplate;
  message?: string;
}

export interface SequenceListResponse {
  success: boolean;
  sequences: TemplateSequence[];
  count: number;
}

export interface SequenceResponse {
  success: boolean;
  sequence: TemplateSequence;
  message?: string;
}

export interface SessionResponse {
  success: boolean;
  session: SessionSummary;
}

export interface TemplatePreview {
  rendered_body: string;
  rendered_header?: string;
  rendered_footer?: string;
  components: any[];
}

export interface TemplatePreviewResponse {
  success: boolean;
  preview: TemplatePreview;
  template_name: string;
}
